import { useState, useMemo, useRef, useEffect, type FormEvent } from 'react';
import { FormError, useFormError } from '@/components/ui/FormError';
import { useTranslation } from 'react-i18next';
import { formatMoney as formatHomeMoney } from '@/lib/money';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { ACTIVE_SESSION_KEY } from '@/hooks/useActiveSession';
import { ArrowLeft, ChevronRight, Plus, Plug, Loader2, Zap, Settings, AlertTriangle } from 'lucide-react';
import { HTTPError } from 'ky';
import { BottomSheet, RevenueChart } from '@evi/ui';
import { PhoneCaptureModal } from '@/components/ui/PhoneCaptureModal';
import { toast } from '@/store/toast';
import {
  fetchMyHome, createMyHome, fetchMyChargers, fetchMyCharger, registerResidentCharger,
  OCPP_WS_BASE, fetchMySessions, startDriverCharger, submitStoreLead, fetchResidentRegisterModels,
  type CreateHomeBody, type MyCharger, type RegisterResidentChargerBody,
  type DriverStartError,
} from '@/lib/api';
import { EarnPromptCard } from '@/components/home/EarnPromptCard';
import { ConnectionDetailsCard } from '@/components/home/ConnectionDetailsCard';
import { HomeAddressFields } from '@/components/home/HomeAddressFields';
import {
  EMPTY_HOME_ADDRESS, formatOrList, homeAddressErrorText, homeAddressForBody, homeAddressRegionCode,
  type HomeAddressDraft,
} from '@/lib/homeAddressDraft';
import { timeZoneCityLabel } from '@evi/shared';
import {
  deviceTimeZone, homeZoneLabel, planSetupTimeZone, setupTimeZoneForBody,
} from '@/lib/homeTimeZone';
import {
  alreadyRegisteredFrom, connectionViewFor, isAwaitingFirstConnection, isNetworkIncompatibleModel,
  supportUrlForIdentity,
} from '@/lib/homeChargerConnection';
import { useAuthStore } from '@/store/auth';
import { type TurnstileInstance } from '@marsidev/react-turnstile';
import { TurnstileGate, TURNSTILE_SITE_KEY } from '@/components/ui/TurnstileGate';
import {
  homeRateFromTenant, hasUsableHomeRate, estimateSessionsUtilityCents, describeHomeRate,
  type HomeUtilityRate,
} from '@/lib/homeRate';
import formStyles from '@/components/Form.module.css';
import styles from './MyHomePage.module.css';
import { MenuIcon, StatusPill } from '@/components/primitives';
import { viewForConnectorStatus } from '@/lib/connectorStatus';
import { startedPlugNotice } from '@/pages/pre-session/startedPlugNotice';

interface Props { onNavigate: (path: string) => void }

const HOME_KEY = ['my-home'] as const;
const CHARGERS_KEY = ['my-chargers'] as const;
const CONNECTOR_TYPES: RegisterResidentChargerBody['connector_type'][] =
  ['J1772', 'NACS', 'CCS1'];

/**
 * My Home -- the living residential dashboard (Residential UX slice A).
 * Ambient hero, charger status + Start charging, and the Home Charging
 * report. Configuration (home profile, utility rate, charging speed,
 * whitelist) lives on Home Settings at /my-home/settings; the row at the
 * bottom links there. Setup states (create home, register charger) are
 * unchanged from earlier slices, except that a registration now ends on
 * the earn question (EarnPromptCard) inside the same sheet: Yes goes to
 * the earn walk at /my-chargers/:id/earn, Not now closes it as before.
 *
 * Register-first (2026-10-02): the charger no longer needs to be online
 * to register. The sheet first shows the connection details (URL, charger
 * ID, password on reveal), then the earn question. The dashboard shows the
 * setup card expanded and a "Waiting for first connection" pill while the
 * charger has never connected, and a collapsed "Connection details" row
 * once it has. Household members never see credentials.
 */
/** A registration the API answered 409 (already registered): what was
 *  sent, and the support link to show with the error. */
interface RegisterConflict {
  body: RegisterResidentChargerBody;
  supportUrl: string;
}

export function MyHomePage({ onNavigate }: Props) {
  const { t } = useTranslation('myHome');
  const qc = useQueryClient();
  const homeQuery = useQuery({ queryKey: HOME_KEY, queryFn: fetchMyHome, staleTime: 60_000 });
  const home = homeQuery.data;
  const hasHome = Boolean(home?.tenant);
  const locationId = home?.location?._id ?? null;
  // Household slice: members ('manager'/'viewer') see the home too, via
  // the charger summary GET /me/home carries. Missing role on a payload
  // with a tenant is a pre-slice owner response.
  const isOwner = (home?.role ?? 'owner') === 'owner';

  // The driver's chargers; the one under the home Location is the home charger.
  // Owner-only (the /my-chargers routes are owner-scoped). Polled at the
  // app's live-charger convention so the card's connector-status pill
  // tracks without a manual refresh: live charger events (the owner hears
  // their own chargers), the 10s foreground poll as the fallback.
  const chargersRefreshMs = useStreamAwareInterval(10_000);
  const chargersQuery = useQuery({
    queryKey: CHARGERS_KEY,
    queryFn: fetchMyChargers,
    refetchInterval: chargersRefreshMs,
    refetchIntervalInBackground: false,
    enabled: hasHome && isOwner,
  });
  const homeCharger = locationId && isOwner
    ? (chargersQuery.data ?? []).find((c) => c.location_id === locationId) ?? null
    : null;

  const [setupOpen, setSetupOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  // Set once a registration lands: the register sheet swaps its form for
  // the connection details, then the earn question, and stays open until
  // the owner answers. Cleared whenever the sheet closes, so a reopen
  // starts on the form.
  const [registered, setRegistered] = useState<MyCharger | null>(null);
  // The 412 path: stash the attempted registration, capture a phone, then retry.
  const [phoneRetryBody, setPhoneRetryBody] = useState<RegisterResidentChargerBody | null>(null);
  // The retry after the phone capture answered 409: the register sheet
  // reopens on that identity with the same "already registered" answer and
  // support link the form shows on a direct 409.
  const [registerConflict, setRegisterConflict] = useState<RegisterConflict | null>(null);
  // The 402 path: the residential 3-charger cap. Registration swaps to
  // the upgrade sheet, which captures intent through the leads endpoint.
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refreshHome = () => qc.invalidateQueries({ queryKey: HOME_KEY });
  const refreshChargers = () => qc.invalidateQueries({ queryKey: CHARGERS_KEY });

  const isLoading = homeQuery.isLoading;
  const error = homeQuery.error;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('subtitle.loading')
              : hasHome
                ? t('subtitle.ready')
                : t('subtitle.setup')}
          </p>
        </div>
      </header>

      {isLoading && <HomeLoading />}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('errorLoad')}
        </div>
      )}

      {!isLoading && !error && !hasHome && (
        <HomeEmpty onSetup={() => setSetupOpen(true)} />
      )}

      {/* Household member view — the owner's data, read through the
          member-aware GET /me/home (report shows the MEMBER's own
          charging at this home). Setup/registration flows stay
          owner-only below. */}
      {!isLoading && !error && hasHome && !isOwner && (
        <>
          <HomeHero />
          {home?.charger
            ? (
              <HomeReport
                chargerId={home.charger._id}
                rate={homeRateFromTenant(home?.tenant ?? null)}
                currency={home?.location?.currency}
                onOpenSettings={() => onNavigate('/my-home/settings')}
                onOpenSession={(id) => onNavigate(`/sessions/${id}`)}
              />
            )
            : (
              <p className={styles.whitelistMuted}>
                {t('noChargerYet')}
              </p>
            )}
          <button
            type="button"
            className={styles.settingsRow}
            onClick={() => onNavigate('/my-home/settings')}
          >
            <Settings size={15} /> {t('homeSettings')}
          </button>
        </>
      )}

      {!isLoading && !error && hasHome && isOwner && (
        chargersQuery.isLoading
          ? <HomeLoading />
          : homeCharger
            ? (
              <>
                <HomeHero />
                <ChargerCard
                  charger={homeCharger}
                  onOpen={() => onNavigate(`/my-chargers/${homeCharger._id}`)}
                />
                {/* Never connected: the setup card, expanded, right under
                    the charger it belongs to. */}
                {homeCharger.connection && isAwaitingFirstConnection(homeCharger) && (
                  <ConnectionDetailsCard
                    chargerId={homeCharger._id}
                    connection={connectionViewFor(homeCharger, OCPP_WS_BASE)}
                    variant="setup"
                    onChanged={() => { void refreshChargers(); }}
                  />
                )}
                <StartChargeSection charger={homeCharger} onNavigate={onNavigate} />
                <HomeReport
                  chargerId={homeCharger._id}
                  rate={homeRateFromTenant(home?.tenant ?? null)}
                  currency={home?.location?.currency}
                  onOpenSettings={() => onNavigate('/my-home/settings')}
                  onOpenSession={(id) => onNavigate(`/sessions/${id}`)}
                />
                {/* Connected: the same details, folded away for a reset or
                    a new router. */}
                {homeCharger.connection && !isAwaitingFirstConnection(homeCharger) && (
                  <ConnectionDetailsCard
                    chargerId={homeCharger._id}
                    connection={connectionViewFor(homeCharger, OCPP_WS_BASE)}
                    variant="details"
                    onChanged={() => { void refreshChargers(); }}
                  />
                )}
                <button
                  type="button"
                  className={styles.settingsRow}
                  onClick={() => onNavigate('/my-home/settings')}
                >
                  <Settings size={15} /> {t('homeSettings')}
                </button>
              </>
            )
            : (
              <>
                <BeforeYouStart />
                {locationId && (
                  <button
                    type="button"
                    className={styles.registerCta}
                    onClick={() => setRegisterOpen(true)}
                  >
                    <Plus size={16} /> {t('registerCta')}
                  </button>
                )}
              </>
            )
      )}

      <HomeSetupSheet
        isOpen={setupOpen}
        onClose={() => setSetupOpen(false)}
        onResolved={async () => { await refreshHome(); setSetupOpen(false); }}
      />

      {locationId && (
        <ChargerRegisterSheet
          isOpen={registerOpen}
          locationId={locationId}
          // The polled row once the list has it, so the setup card tracks
          // the charger (window closing, provisioning, connected) while the
          // sheet stays open; the 201 until then.
          registered={registered
            ? (chargersQuery.data ?? []).find((c) => c._id === registered._id) ?? registered
            : null}
          onConnectionChanged={() => { void refreshChargers(); }}
          initialConflict={registerConflict}
          onClose={() => { setRegisterOpen(false); setRegistered(null); setRegisterConflict(null); }}
          // The sheet stays open on success: the chargers refetch flips the
          // page behind it to the dashboard while the setup details and
          // then the earn question show.
          onRegistered={async (created) => { await refreshChargers(); setRegistered(created); }}
          onEarn={(id) => onNavigate(`/my-chargers/${id}/earn`)}
          onPhoneRequired={(body) => { setRegisterOpen(false); setRegisterConflict(null); setPhoneRetryBody(body); }}
          onUpgradeRequired={() => { setRegisterOpen(false); setUpgradeOpen(true); }}
          onContactSupport={(url) => { setRegisterOpen(false); onNavigate(url); }}
        />
      )}

      <ResidentialUpgradeSheet
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />

      {phoneRetryBody && (
        <PhoneCaptureModal
          reason={t('phone.reason')}
          onCapture={async () => {
            // The modal already saved the phone; retry the stashed registration.
            const body = phoneRetryBody;
            try {
              const created = await registerResidentCharger(body);
              void useAuthStore.getState().refreshUser(); // persona → homeowner
              await refreshChargers();
              toast(t('toast.chargerRegistered'), { tone: 'accent' });
              // Same registration, one modal later, so the same next steps:
              // reopen the sheet on the connection details.
              setRegistered(created);
              setRegisterOpen(true);
            } catch (err) {
              const status = err instanceof HTTPError ? err.response.status : null;
              if (status === 402) {
                // Residential 3-charger cap. The phone saved fine;
                // surface the upgrade path instead of a dead-end toast.
                setUpgradeOpen(true);
              } else if (status === 409 && err instanceof HTTPError) {
                // Already registered: the same answer as the form's, with
                // the support link, not a toast that fades in two seconds.
                let taken: { supportUrl: string } | null = null;
                try { taken = alreadyRegisteredFrom(await err.response.clone().json()); } catch { /* not JSON */ }
                setRegisterConflict({ body, supportUrl: taken?.supportUrl ?? supportUrlForIdentity(body.identity) });
                setRegistered(null);
                setRegisterOpen(true);
              } else {
                toast(
                  status === 429
                    ? t('registerForm.errorRateLimited')
                    : t('toast.phoneSavedNotRegistered'),
                  { tone: 'warn' },
                );
              }
            } finally {
              setPhoneRetryBody(null);
            }
          }}
          onSkip={() => setPhoneRetryBody(null)}
        />
      )}
    </div>
  );
}

// ── Before you start: register first, then connect ────────────────────────
// Register-first (2026-10-02): register here, then enter the URL, charger
// ID and password My Home shows into the Autel app, then wait for Online.
// The connection details live on the setup card after registering, so no
// address is shown before there is a charger to connect.
function BeforeYouStart() {
  const { t } = useTranslation('myHome');
  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('beforeYouStart.title')}</h2>
      <p className={styles.guideIntro}>
        {t('beforeYouStart.intro')}
      </p>
      <ol className={styles.steps}>
        <li className={styles.step}>
          <span className={styles.stepNum} aria-hidden>1</span>
          <span className={styles.stepText}>
            {t('beforeYouStart.step1')}
          </span>
        </li>
        <li className={styles.step}>
          <span className={styles.stepNum} aria-hidden>2</span>
          <span className={styles.stepText}>
            {t('beforeYouStart.step2')}
          </span>
        </li>
        <li className={styles.step}>
          <span className={styles.stepNum} aria-hidden>3</span>
          <span className={styles.stepText}>
            {t('beforeYouStart.step3')}
          </span>
        </li>
      </ol>
    </section>
  );
}

// ── Registered charger card: live status pill + tap-through to detail ─────
function ChargerCard({ charger, onOpen }: { charger: MyCharger; onOpen: () => void }) {
  const { t } = useTranslation('myHome');
  const connector = charger.connectors[0];
  const named = charger.vendor && charger.vendor !== 'Unknown';
  const title = named
    ? `${charger.vendor}${charger.model && charger.model !== 'Unknown' ? ` ${charger.model}` : ''}`
    : t('chargerCard.homeCharger');
  const meta = [connector?.type, connector ? `${connector.max_power_kw} kW` : null]
    .filter(Boolean)
    .join(' · ');
  // Same canonical mapping the public station page uses for its
  // connector rows: Offline wins over any stale OCPP state, else the
  // live connector status (Available / Plugged in / Charging /
  // Finishing / Paused / ...). Soft pill, same as the public rows.
  // A charger that has never connected is not "Offline": it is waiting
  // for its first connection (register-first).
  const awaiting = isAwaitingFirstConnection(charger);
  const status = viewForConnectorStatus(connector?.status, charger.is_online);
  const view = awaiting ? { tone: 'info' as const, label: t('connection.waitingPill') } : status;
  // The waiting label is long. Beside the body it took the whole row on a
  // phone and squeezed the name, connector and charger ID out, so while
  // the charger waits the pill sits under the charger ID instead.
  const pill = (
    <StatusPill tone={view.tone} variant="soft" size="sm" className={awaiting ? styles.cardPillInline : styles.cardPill}>
      {view.label}
    </StatusPill>
  );
  return (
    <button type="button" className={styles.card} onClick={onOpen}>
      <div className={styles.cardIcon} aria-hidden><Plug size={22} /></div>
      <div className={styles.cardBody}>
        <p className={styles.cardTitle}>{title}</p>
        {meta && <p className={styles.cardMeta}>{meta}</p>}
        <p className={styles.cardSerial}>{t('chargerCard.serial', { serial: charger.identity })}</p>
        {awaiting && pill}
      </div>
      {!awaiting && pill}
      <ChevronRight size={15} className={styles.cardChevron} aria-hidden />
    </button>
  );
}

// ── Start charging: owner remote start at the home charger ────────────────
// Calls the existing driver-start endpoint directly. The PreSessionPage route
// cannot render here: GET /locations/driver/:id filters map-hidden locations
// and a residential home Location is hidden by design, so that page 404s its
// location query and hard-blocks. The backend start path itself (d8eae09)
// resolves the owner and skips the guest and wallet gates for a residential
// free start, so a direct call is the whole job. On success this navigates to
// the same active-charging sentinel route PreSessionPage uses.
function StartChargeSection({
  charger, onNavigate,
}: {
  charger: MyCharger;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('myHome');
  const qc = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<DriverStartError | null>(null);
  const awaiting = isAwaitingFirstConnection(charger);

  const start = async (): Promise<void> => {
    setStartError(null);
    setStarting(true);
    try {
      const started = await startDriverCharger(charger.identity, {
        connector_id: charger.connectors[0]?.connector_id ?? 1,
      });
      // The pending shell exists server-side now; refetch the shared
      // live-session cache so the home (HomeRoute) and the mini bar see
      // it at once rather than one 15s poll later.
      qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      // The Api started the other plug, where the car is plugged in
      // (Warren, 29 Oct 2026): say which, as PreSessionPage does.
      const plugNotice = startedPlugNotice(started);
      if (plugNotice) {
        toast(t('toast.startedOtherPlug', { ns: 'preSession', plug: plugNotice.plug }), { tone: 'accent', durationMs: 6000 });
      } else {
        toast(t('toast.sessionStarted'), { tone: 'accent' });
      }
      onNavigate(`/charging/active?charger=${encodeURIComponent(charger.identity)}`);
    } catch (err) {
      // startDriverCharger throws a structured DriverStartError, not an Error.
      setStartError(err as DriverStartError);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className={styles.startSection}>
      <button
        type="button"
        className={styles.registerCta}
        // Not before the first connection completes: a provisioning
        // charger can hold a socket that admits no session yet.
        disabled={!charger.is_online || awaiting || starting}
        onClick={start}
      >
        {starting ? <Loader2 size={16} className={styles.spin} /> : <Zap size={16} />}
        {starting ? t('start.starting') : t('start.startCharging')}
      </button>
      {(!charger.is_online || awaiting) && (
        <p className={styles.startNote}>
          {awaiting ? t('start.neverConnectedNote') : t('start.offlineNote')}
        </p>
      )}
      {startError && (
        <div className={styles.errorBox}>
          {startError.error}
          {startError.hint ? ` ${startError.hint}` : ''}
        </div>
      )}
    </div>
  );
}

// ── Ambient hero (Residential UX slice A) ─────────────────────────────────
// Small muted loop of the wallbox product film, encoded separately from the
// store's film.mp4 (720 wide, ~1.2 MB, no audio track). Reduced-motion
// users and the load window get the poster still instead; both assets sit
// in a rounded card that reads fine on dark and light themes.
function HomeHero() {
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div className={styles.heroWrap} aria-hidden>
      {reduceMotion ? (
        <img
          className={styles.heroMedia}
          src="/media/products/wallbox/hero-loop-poster.jpg"
          alt=""
        />
      ) : (
        <video
          className={styles.heroMedia}
          src="/media/products/wallbox/hero-loop.mp4"
          poster="/media/products/wallbox/hero-loop-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
        />
      )}
    </div>
  );
}

// ── Home charging report (read-only, owner's own sessions) ────────────────
const REPORT_PERIODS = [
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '1y', label: '1 year', days: 365 },
  { key: 'all', label: 'All', days: null },
] as const;
type ReportPeriodKey = typeof REPORT_PERIODS[number]['key'];

function formatDuration(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

function formatSessionDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function HomeReport({
  chargerId, rate, currency, onOpenSettings, onOpenSession,
}: {
  chargerId: string;
  rate: HomeUtilityRate | null;
  /** The home's currency (GET /me/home location.currency, item 3): the
   *  owner's electricity rate and every estimate are in it. Absent from
   *  an older Api: USD. */
  currency?: string | null;
  /** Rate entry moved to Home Settings; the report deep-links there. */
  onOpenSettings: () => void;
  /** Routes to the shared SessionDetailPage (/sessions/:id) so back
   *  returns here naturally; the detail route already serves the
   *  caller's own sessions, and every home session IS the caller's. */
  onOpenSession: (sessionId: string) => void;
}) {
  const { t, i18n } = useTranslation('myHome');
  const [period, setPeriod] = useState<ReportPeriodKey>('30d');
  const formatMoney = (cents: number): string => formatHomeMoney(cents, currency, i18n.language);

  // The driver's own sessions (user-scoped me/sessions), filtered client-side to
  // the home charger. fetchMySessions takes no charger filter, so the page of
  // recent sessions is narrowed here. This shows the owner's OWN home charging;
  // a charger-scoped read for whitelisted friends is a later slice.
  const sessionsQ = useQuery({
    queryKey: ['my-home-sessions'],
    queryFn: () => fetchMySessions({ limit: 100 }),
    staleTime: 30_000,
  });

  const homeSessions = useMemo(
    () => (sessionsQ.data?.data ?? []).filter((s) => s.charger_id === chargerId),
    [sessionsQ.data, chargerId],
  );

  const filtered = useMemo(() => {
    const days = REPORT_PERIODS.find((p) => p.key === period)?.days ?? null;
    if (days === null) return homeSessions;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return homeSessions.filter((s) => new Date(s.started_at).getTime() >= cutoff);
  }, [homeSessions, period]);

  const stats = useMemo(() => {
    const count = filtered.length;
    const totalKwh = filtered.reduce((sum, s) => sum + (s.energy_kwh ?? 0), 0);
    const totalMin = filtered.reduce((sum, s) => sum + (s.duration_minutes ?? 0), 0);
    return { count, totalKwh, avgMin: count > 0 ? totalMin / count : 0 };
  }, [filtered]);

  // Informational cost only: the owner's own rate structure (flat / TOU /
  // tiered, lib/homeRate.ts) applied to each session CLIENT-SIDE for display.
  // Estimated over ALL known home sessions so tiered brackets accumulate
  // per calendar month honestly, then summed for the displayed period. It is
  // never written back to any billing field; the home charger is 0-rate and free.
  const rateUsable = hasUsableHomeRate(rate);
  const sessionCostById = useMemo(
    () => estimateSessionsUtilityCents(rate, homeSessions),
    [rate, homeSessions],
  );
  const estCostCents = rateUsable
    ? filtered.reduce((sum, s) => sum + (sessionCostById.get(s._id) ?? 0), 0)
    : null;

  // Energy buckets for the shared RevenueChart, computed from the SAME
  // filtered home sessions the stats use. Bucket granularity is span-
  // derived like the History chart's server route: daily to ~5 weeks,
  // weekly to ~6 months, monthly beyond. Home charging is free, so the
  // chart's dimension is kWh, the Reports-Energy pattern (titleLabel +
  // formatY), not spend.
  const energyBuckets = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const days = REPORT_PERIODS.find((p) => p.key === period)?.days ?? null;
    const now = Date.now();
    const start = days !== null
      ? now - days * dayMs
      : homeSessions.length > 0
        ? Math.min(...homeSessions.map((s) => new Date(s.started_at).getTime()))
        : now - 30 * dayMs;
    const spanMs = Math.max(dayMs, now - start);
    const bucketMs = spanMs <= 35 * dayMs ? dayMs : spanMs <= 190 * dayMs ? 7 * dayMs : 30 * dayMs;
    const count = Math.max(1, Math.ceil(spanMs / bucketMs));
    const buckets = new Array<number>(count).fill(0);
    for (const s of filtered) {
      const t = new Date(s.started_at).getTime();
      const idx = Math.max(0, Math.min(count - 1, Math.floor((t - start) / bucketMs)));
      buckets[idx] = Math.round((buckets[idx]! + (s.energy_kwh ?? 0)) * 10) / 10;
    }
    return buckets;
  }, [filtered, homeSessions, period]);

  return (
    <section className={styles.guide}>
      <h2 className={styles.guideTitle}>{t('report.title')}</h2>

      <div className={styles.periodRow} role="radiogroup" aria-label={t('report.periodAria')}>
        {REPORT_PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={period === p.key}
            className={period === p.key ? styles.periodChipActive : styles.periodChip}
            onClick={() => setPeriod(p.key)}
          >
            {t(`report.period.${p.key}`)}
          </button>
        ))}
      </div>

      {sessionsQ.isLoading && <p className={styles.whitelistMuted}>{t('report.loading')}</p>}

      {!sessionsQ.isLoading && sessionsQ.error && (
        <div className={styles.errorBox}>{t('report.errorLoad')}</div>
      )}

      {!sessionsQ.isLoading && !sessionsQ.error && (
        <>
          {/* Same shared chart the History page uses, in its energy
              dimension; the section's period chips above drive it, so the
              chart's own chip rail is suppressed (allowedPeriods empty). */}
          <div className={styles.homeChartWrap}>
            <RevenueChart
              data={energyBuckets}
              currentRate={0}
              period={period}
              allowedPeriods={[]}
              onPeriodChange={() => {}}
              showRate={false}
              footer={null}
              titleLabel={t('report.energyChartLabel')}
              formatY={(n) => `${Math.round(n * 10) / 10}`}
            />
          </div>

          <div className={styles.statRow}>
            <Stat label={t('stat.energy')} value={`${stats.totalKwh.toFixed(1)} kWh`} />
            <Stat label={t('stat.sessions')} value={String(stats.count)} />
            <Stat label={t('stat.avgSession')} value={stats.count > 0 ? formatDuration(stats.avgMin) : '--'} />
          </div>

          <div className={styles.rateBlock}>
            {rateUsable ? (
              <div className={styles.rateSummary}>
                <div className={styles.rateCostMain}>
                  <span className={styles.rateCostValue}>{formatMoney(estCostCents ?? 0)}</span>
                  <span className={styles.rateCostLabel}>{t('report.estCostLabel')}</span>
                </div>
                <p className={styles.rateNote}>
                  {t('report.rateNote', { rate: describeHomeRate(rate, formatMoney) })}
                </p>
                <button
                  type="button"
                  className={styles.rateEditBtn}
                  onClick={onOpenSettings}
                >
                  {t('report.editRate')}
                </button>
              </div>
            ) : (
              <div className={styles.rateSummary}>
                <p className={styles.rateNote}>
                  {t('report.addRateNote')}
                </p>
                <button
                  type="button"
                  className={styles.rateEditBtn}
                  onClick={onOpenSettings}
                >
                  {t('report.setRate')}
                </button>
              </div>
            )}
          </div>

          {filtered.length === 0
            ? (
              <p className={styles.whitelistMuted}>
                {homeSessions.length === 0
                  ? t('report.emptyNoSessions')
                  : t('report.emptyNoPeriod')}
              </p>
            )
            : (
              <ul className={styles.sessionList}>
                {filtered.map((s) => (
                  <li key={s._id} className={styles.sessionRow}>
                    <button
                      type="button"
                      className={styles.sessionRowBtn}
                      onClick={() => onOpenSession(s._id)}
                    >
                      <span className={styles.sessionDate}>{formatSessionDate(s.started_at)}</span>
                      <span className={styles.sessionMeta}>
                        {(s.energy_kwh ?? 0).toFixed(1)} kWh · {formatDuration(s.duration_minutes)}
                        {rateUsable && ` · ${formatMoney(sessionCostById.get(s._id) ?? 0)}`}
                      </span>
                      <ChevronRight size={15} className={styles.sessionChevron} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

// ── Charger registration form ─────────────────────────────────────────────
// Two faces on one sheet: the form, then — once a registration lands —
// the earn question in its place. Not now closes the sheet as the old
// success path did; Yes hands off to the earn walk.
function ChargerRegisterSheet({
  isOpen, locationId, registered, onClose, onRegistered, onEarn,
  onPhoneRequired, onUpgradeRequired, onContactSupport, onConnectionChanged, initialConflict,
}: {
  isOpen: boolean;
  onContactSupport: (supportUrl: string) => void;
  /** After a re-armed first-connection window, so the page refetches. */
  onConnectionChanged: () => void;
  /** A 409 from the phone-capture retry: the form opens on it. */
  initialConflict: RegisterConflict | null;
  locationId: string;
  /** The charger just registered, or null while the form is up. */
  registered: MyCharger | null;
  onClose: () => void;
  onRegistered: (created: MyCharger) => void | Promise<void>;
  onEarn: (chargerId: string) => void;
  onPhoneRequired: (body: RegisterResidentChargerBody) => void;
  onUpgradeRequired: () => void;
}) {
  const { t } = useTranslation('myHome');
  // Register-first: a registration lands on the connection details (what
  // to type into the Autel app), then Next moves to the earn question. A
  // response with no connection block (an older API) goes straight to the
  // earn question as before.
  const [stage, setStage] = useState<'setup' | 'earn'>('setup');
  useEffect(() => { if (!registered) setStage('setup'); }, [registered]);
  const showSetup = !!registered?.connection && stage === 'setup';
  // The setup card under the header carries its own "Set up your charger"
  // title, so the header says what happened instead of repeating it.
  const title = !registered ? t('sheet.registerTitle') : t('sheet.registeredTitle');
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      glass
    >
      {isOpen && registered && showSetup && (
        <div className={styles.upgradeBody}>
          <ConnectionDetailsCard
            chargerId={registered._id}
            connection={connectionViewFor(registered, OCPP_WS_BASE)}
            variant="setup"
            onChanged={onConnectionChanged}
          />
          <button type="button" className={styles.registerCta} onClick={() => setStage('earn')}>
            {t('sheet.next')}
          </button>
        </div>
      )}
      {isOpen && registered && !showSetup && (
        <HomeEarnPrompt
          chargerId={registered._id}
          onYes={() => onEarn(registered._id)}
          onNotNow={onClose}
        />
      )}
      {isOpen && !registered && (
        <ChargerRegisterForm
          locationId={locationId}
          onClose={onClose}
          onRegistered={onRegistered}
          onPhoneRequired={onPhoneRequired}
          onUpgradeRequired={onUpgradeRequired}
          onContactSupport={onContactSupport}
          initialConflict={initialConflict}
        />
      )}
    </BottomSheet>
  );
}

// ── Earn question after registration ─────────────────────────────────────
// The margin line needs the owner's electricity estimate in the
// min/max shape the charger page uses, and only the charger detail
// carries it (home_monetization.utility_rate) — the /me/home tenant
// payload has the raw windows/tiers, not the resolved range. One read
// on the charger's own query key, so the charger page and the walk
// start warm whichever button is tapped.
function HomeEarnPrompt({
  chargerId, onYes, onNotNow,
}: {
  chargerId: string;
  onYes: () => void;
  onNotNow: () => void;
}) {
  const { t } = useTranslation('myHome');
  const detailQ = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
    staleTime: 60_000,
  });
  // undefined while loading holds the line back; a failed read shows
  // the noRate wording, which is still true.
  const utilityRate = detailQ.isLoading
    ? undefined
    : detailQ.data?.home_monetization?.utility_rate ?? null;
  // The charger's EVI fee once the detail has it; undefined (loading or
  // a failed read) lets the card quote the default a new charger gets.
  const connectionFeeCents = detailQ.data?.home_monetization?.connection_fee_cents;
  // The home's currency (item 3); undefined while loading reads USD.
  const currency = detailQ.data?.home_monetization?.currency;
  return (
    <div className={styles.upgradeBody}>
      <EarnPromptCard
        title={t('earnPrompt.title')}
        subtitle={t('earnPrompt.subtitle')}
        utilityRate={utilityRate}
        connectionFeeCents={connectionFeeCents}
        currency={currency}
        primaryLabel={t('earnPrompt.yes')}
        onPrimary={onYes}
        secondaryLabel={t('earnPrompt.notNow')}
        onSecondary={onNotNow}
      />
    </div>
  );
}

// ── Residential upgrade sheet (3-charger cap) ─────────────────────────────
// Shown when resident-register returns 402 UPGRADE_REQUIRED. Captures
// intent through the store leads endpoint (source residential_upgrade,
// user_id attached server-side); self-serve conversion to an operator
// plan is a future arc, this slice enforces and captures.
function ResidentialUpgradeSheet({
  isOpen, onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('myHome');
  const email = useAuthStore((s) => s.user?.email ?? '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Same Turnstile posture as the electrician capture: this sheet
  // posts to the same /store/leads endpoint, so once a server secret
  // is configured a token is required here too.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // The sheet stays mounted while closed (BottomSheet renders null),
  // so drop any solved token on close — a reopen otherwise enables
  // the CTA with a stale challenge that expires after 5 minutes.
  useEffect(() => {
    if (!isOpen) setTurnstileToken(null);
  }, [isOpen]);

  const request = async (): Promise<void> => {
    if (TURNSTILE_SITE_KEY && !turnstileToken) return;
    setSending(true);
    try {
      await submitStoreLead(email, 'residential_upgrade', turnstileToken ?? undefined);
      setSent(true);
    } catch (err) {
      // A rejected token is spent — reset the widget for a fresh
      // challenge instead of resubmitting a dead token.
      if (err instanceof HTTPError) {
        try {
          const body = await err.response.clone().json() as { error?: { code?: string } };
          if (body?.error?.code === 'TURNSTILE_FAILED' || body?.error?.code === 'TURNSTILE_REQUIRED') {
            turnstileRef.current?.reset();
            setTurnstileToken(null);
          }
        } catch {
          // Body wasn't JSON — nothing captcha-specific to reset.
        }
      }
      toast(t('toast.requestFailed'), { tone: 'warn' });
    } finally {
      setSending(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('upgrade.title')} glass>
      {sent ? (
        <div className={styles.upgradeBody}>
          <p className={styles.upgradeText}>
            {t('upgrade.sentBody')}
          </p>
          <button type="button" className={styles.registerCta} onClick={onClose}>
            {t('action.done')}
          </button>
        </div>
      ) : (
        <div className={styles.upgradeBody}>
          <p className={styles.upgradeText}>
            {t('upgrade.body')}
          </p>
          <TurnstileGate
            ref={turnstileRef}
            posture="open"
            className={styles.turnstileWrap}
            onToken={setTurnstileToken}
          />
          <button
            type="button"
            className={styles.registerCta}
            onClick={request}
            disabled={sending || !email || Boolean(TURNSTILE_SITE_KEY && !turnstileToken)}
          >
            {sending ? <Loader2 size={16} className={styles.spin} /> : null}
            {sending ? t('upgrade.sending') : t('upgrade.request')}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

async function readHttpErrorMessage(err: HTTPError): Promise<string | null> {
  return (await readHttpErrorEnvelope(err))?.message ?? null;
}

/** The error envelope's message plus, for a refused home address, the
 *  rule it broke (`error.reason`, an @evi/shared HomeAddressErrorCode). */
async function readHttpErrorEnvelope(
  err: HTTPError,
): Promise<{ message: string | null; reason: string | null } | null> {
  try {
    const body = await err.response.clone().json() as { error?: { message?: string; reason?: string } };
    return { message: body?.error?.message ?? null, reason: body?.error?.reason ?? null };
  } catch {
    return null;
  }
}

function ChargerRegisterForm({
  locationId, onClose, onRegistered, onPhoneRequired, onUpgradeRequired, onContactSupport, initialConflict,
}: {
  locationId: string;
  onClose: () => void;
  onContactSupport: (supportUrl: string) => void;
  /** Opens on an earlier 409 (the phone-capture retry): its fields, the
   *  error and the support link. */
  initialConflict?: RegisterConflict | null;
  onRegistered: (created: MyCharger) => void | Promise<void>;
  onPhoneRequired: (body: RegisterResidentChargerBody) => void;
  onUpgradeRequired: () => void;
}) {
  const { t } = useTranslation('myHome');
  const { t: tEarn } = useTranslation('earn');
  const seed = initialConflict?.body;
  const [identity, setIdentity] = useState(seed?.identity ?? '');
  const [connector, setConnector] = useState<RegisterResidentChargerBody['connector_type']>(seed?.connector_type ?? 'J1772');
  const [power, setPower] = useState(seed ? String(seed.max_power_kw) : '');
  // Optional model (register-first): '' is "Other / not sure". A model that
  // uses first-connection setup is decided by the API, not here.
  const [model, setModel] = useState(seed?.model ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useFormError();
  // Set on the 409: the form shows the support link under the error.
  const [supportUrl, setSupportUrl] = useState<string | null>(initialConflict?.supportUrl ?? null);
  useEffect(() => {
    if (initialConflict) setErrorMsg(t('registerForm.errorConflict'));
  }, [initialConflict, setErrorMsg, t]);
  const modelsQ = useQuery({
    queryKey: ['resident-register-models'],
    queryFn: fetchResidentRegisterModels,
    staleTime: 10 * 60_000,
  });
  const models = modelsQ.data ?? [];
  const pickedIncompatible = model !== '' && (
    isNetworkIncompatibleModel(model)
    || models.some((m) => m.model === model && !m.network_compatible)
  );

  const powerNum = Number(power);
  const canSubmit =
    /^[A-Za-z0-9_-]{1,50}$/.test(identity.trim())
    && Number.isFinite(powerNum) && powerNum >= 1 && powerNum <= 350
    && !submitting;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg('');
    setSupportUrl(null);
    const body: RegisterResidentChargerBody = {
      identity: identity.trim(),
      location_id: locationId,
      connector_type: connector,
      max_power_kw: powerNum,
      ...(model ? { model } : {}),
    };
    try {
      const created = await registerResidentCharger(body);
      void useAuthStore.getState().refreshUser(); // persona → homeowner
      toast(t('toast.chargerRegistered'), { tone: 'accent' });
      // The sheet swaps this form for the connection details; no close here.
      await onRegistered(created);
    } catch (err) {
      if (err instanceof HTTPError) {
        const status = err.response.status;
        if (status === 412) {
          // Needs a phone first. Hand the body up so the page captures a phone
          // and retries, and drop this form's submitting state.
          setSubmitting(false);
          onPhoneRequired(body);
          return;
        }
        if (status === 402) {
          // Residential 3-charger cap — resident-register's only 402
          // is UPGRADE_REQUIRED. The page swaps to the upgrade sheet.
          setSubmitting(false);
          onUpgradeRequired();
          return;
        }
        if (status === 409) {
          // Already registered (by anyone, for any reason): one answer,
          // and the support form, which takes ?charger= as context.
          let taken: { supportUrl: string } | null = null;
          try { taken = alreadyRegisteredFrom(await err.response.clone().json()); } catch { /* not JSON */ }
          setErrorMsg(t('registerForm.errorConflict'));
          setSupportUrl(taken?.supportUrl ?? supportUrlForIdentity(body.identity));
          setSubmitting(false);
          return;
        }
        if (status === 429) {
          // The per-user registration limit (10 an hour): the server's
          // message is English-only, so say it here.
          setErrorMsg(t('registerForm.errorRateLimited'));
          setSubmitting(false);
          return;
        }
        if (status === 400) {
          // HOME_COUNTRY_REQUIRED (item 3): the home's address names no
          // country, so there is no currency for its tariff. Said here in
          // the driver's language; the server's message is English.
          let code: string | null = null;
          try {
            code = ((await err.response.clone().json()) as { error?: { code?: string } })?.error?.code ?? null;
          } catch { /* not JSON */ }
          if (code === 'HOME_COUNTRY_REQUIRED') {
            setErrorMsg(tEarn('price.validation.countryRequired'));
            setSubmitting(false);
            return;
          }
        }
        const serverMsg = await readHttpErrorMessage(err);
        setErrorMsg(serverMsg ?? t('registerForm.errorGeneric'));
        setSubmitting(false);
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : t('registerForm.errorGenericShort'));
      setSubmitting(false);
    }
  };

  return (
    <form className={`${formStyles.form} ${styles.sheetForm}`} onSubmit={handleSubmit} noValidate>
      {errorMsg && <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>}
      {supportUrl && (
        // The support form reads ?charger= as the ticket's context.
        <button type="button" className={styles.rateEditBtn} onClick={() => onContactSupport(supportUrl)}>
          {t('registerForm.contactSupport')}
        </button>
      )}

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ch-identity">{t('registerForm.serialLabel')}</label>
        <input
          id="ch-identity" className={formStyles.input} value={identity}
          onChange={(e) => setIdentity(e.target.value)}
          placeholder={t('registerForm.serialPlaceholder')} maxLength={50}
          autoCapitalize="characters" autoCorrect="off" spellCheck={false}
          disabled={submitting} required
        />
        <p className={styles.fieldHint}>
          {t('registerForm.serialHint')}
        </p>
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ch-model">{t('registerForm.modelLabel')}</label>
        <select
          id="ch-model" className={formStyles.input} value={model}
          onChange={(e) => setModel(e.target.value)} disabled={submitting}
        >
          <option value="">{t('registerForm.modelOther')}</option>
          {models.map((m) => (
            <option key={m.model} value={m.model}>{m.model}</option>
          ))}
        </select>
        {pickedIncompatible && (
          <p className={styles.fieldHint} role="alert">
            <AlertTriangle size={12} aria-hidden /> {t('registerForm.modelIncompatible', { model })}
          </p>
        )}
      </div>

      <div className={formStyles.field}>
        <span className={formStyles.label}>{t('registerForm.connectorLabel')}</span>
        <div className={styles.chipGroup} role="radiogroup" aria-label={t('registerForm.connectorAria')}>
          {CONNECTOR_TYPES.map((opt) => (
            <button
              key={opt} type="button" role="radio" aria-checked={connector === opt}
              className={connector === opt ? styles.chipActive : styles.chip}
              onClick={() => setConnector(opt)} disabled={submitting}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ch-power">{t('registerForm.powerLabel')}</label>
        <input
          id="ch-power" className={formStyles.input} type="number" inputMode="decimal"
          value={power} onChange={(e) => setPower(e.target.value)}
          placeholder={t('registerForm.powerPlaceholder')} min={1} max={350} step="0.1" disabled={submitting} required
        />
      </div>

      <div className={styles.formActions}>
        <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={submitting}>
          {t('action.cancel')}
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={!canSubmit}>
          {submitting
            ? <><Loader2 size={14} className={styles.spin} /> {t('registerForm.registering')}</>
            : t('registerForm.submit')}
        </button>
      </div>
    </form>
  );
}

// ── Home creation (slice A) ───────────────────────────────────────────────
function HomeEmpty({ onSetup }: { onSetup: () => void }) {
  const { t } = useTranslation('myHome');
  return (
    <div className={styles.empty}>
      {/* My Home hero icon slot — the my-home animated WebP from the menu
          icon family fills the circular slot (poster under reduced motion);
          replaces the old myhome.json Lottie. */}
      <div className={styles.emptyIcon} aria-hidden><MenuIcon name="my-home" fill alt="" /></div>
      <h2 className={styles.emptyTitle}>{t('empty.title')}</h2>
      <p className={styles.emptyDesc}>
        {t('empty.desc')}
      </p>
      <button type="button" className={styles.emptyCta} onClick={onSetup}>
        <Plus size={16} /> {t('empty.cta')}
      </button>
    </div>
  );
}

function HomeLoading() {
  return (
    <div className={styles.list} aria-busy aria-live="polite">
      <div className={styles.cardSkeleton} />
    </div>
  );
}

function HomeSetupSheet({
  isOpen, onClose, onResolved,
}: {
  isOpen: boolean;
  onClose: () => void;
  onResolved: () => void | Promise<void>;
}) {
  const { t } = useTranslation('myHome');
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('setup.sheetTitle')} glass>
      {/* Mounts fresh on each open so the form starts empty. */}
      {isOpen && <HomeSetupForm onClose={onClose} onResolved={onResolved} />}
    </BottomSheet>
  );
}

function HomeSetupForm({
  onClose, onResolved,
}: {
  onClose: () => void;
  onResolved: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('myHome');
  const { t: tAddress } = useTranslation('homeAddress');
  const [nickname, setNickname] = useState('');
  // Country, street, city, region, postal (HomeAddressFields). No country
  // is preselected: the host taps United States or Canada.
  const [draft, setDraft] = useState<HomeAddressDraft>(EMPTY_HOME_ADDRESS);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useFormError();

  // The home's zone (item 4): the phone's, unless the phone keeps none of
  // the clocks the address allows, in which case the host picks one below.
  const deviceZone = useMemo(() => deviceTimeZone(), []);
  const regionCode = homeAddressRegionCode(draft);
  const zonePlan = useMemo(() => planSetupTimeZone(deviceZone, regionCode), [deviceZone, regionCode]);
  // The zone the host picked, one of the question's.
  const [zoneChoice, setZoneChoice] = useState<string | null>(null);
  // A new question needs a new answer.
  const askKey = zonePlan.kind === 'ask' ? `${zonePlan.device}|${zonePlan.address.join(',')}` : '';
  useEffect(() => { setZoneChoice(null); }, [askKey]);
  const bodyZone = setupTimeZoneForBody(zonePlan, zoneChoice);
  // The one-line zone note under the address when nothing needs asking;
  // "set it later" only once the region is known to settle nothing.
  const zoneLine = zonePlan.kind === 'device'
    ? t('setup.timezone.fromPhone', { zone: homeZoneLabel(zonePlan.zone, i18n.language) })
    : zonePlan.kind === 'address'
      ? t('setup.timezone.fromAddress', { zone: homeZoneLabel(zonePlan.zone, i18n.language) })
      : zonePlan.kind === 'none' && regionCode
        ? t('setup.timezone.none')
        : null;

  const address = homeAddressForBody(draft);
  // Disabled until a country is chosen, the fields validate and, when the
  // clocks disagree, a zone is picked.
  const canSubmit = address !== null && bodyZone !== null && !submitting;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit || !address) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      const body: CreateHomeBody = {
        // Nickname optional; the route defaults it to "My Home" when omitted.
        ...(nickname.trim() ? { name: nickname.trim() } : {}),
        address,
        // Left out when the phone has no usable zone: the Api then uses
        // the address zone when the state settles one. The basis only
        // feeds the Api's audit row.
        ...(bodyZone ? { timezone: bodyZone.timezone, timezone_source: bodyZone.source } : {}),
      };
      await createMyHome(body);
      toast(t('toast.homeCreated'), { tone: 'accent' });
      await onResolved();
    } catch (err) {
      // A home already exists (a race, or a stale empty cache): show it rather
      // than erroring. The refresh in onResolved swaps to the has-a-home view.
      if (err instanceof HTTPError && err.response.status === 409) {
        await onResolved();
        return;
      }
      // An address the Api refused names its rule: show it in the reader's
      // language, else the server's own words, never ky's status line.
      const refused = err instanceof HTTPError ? await readHttpErrorEnvelope(err) : null;
      const localized = refused?.reason
        ? homeAddressErrorText(tAddress, refused.reason, draft.country, draft.state)
        : null;
      setErrorMsg(localized ?? refused?.message ?? t('setup.errorGeneric'));
      setSubmitting(false);
    }
  };

  return (
    <form className={`${formStyles.form} ${styles.sheetForm}`} onSubmit={handleSubmit} noValidate>
      {errorMsg && <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>}

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="home-nickname">{t('setup.nameLabel')}</label>
        <input
          id="home-nickname" className={formStyles.input} value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder={t('setup.namePlaceholder')} maxLength={120} disabled={submitting}
        />
      </div>

      <HomeAddressFields idPrefix="home" value={draft} onChange={setDraft} disabled={submitting} />

      {zonePlan.kind === 'ask' ? (
        <div className={formStyles.field} role="group" aria-labelledby="home-zone-title">
          <span className={formStyles.label} id="home-zone-title">{t('setup.timezone.askTitle')}</span>
          <p className={styles.fieldHint}>
            {t('setup.timezone.askBody', {
              device: timeZoneCityLabel(zonePlan.device),
              // One city, or both halves of a split state ("Denver or
              // Chicago").
              address: formatOrList(zonePlan.address.map(timeZoneCityLabel), i18n.language),
            })}
          </p>
          <div className={styles.chipGroup} role="radiogroup" aria-labelledby="home-zone-title">
            {[...zonePlan.address, zonePlan.device].map((zone) => (
              <button
                key={zone}
                type="button"
                role="radio"
                aria-checked={zoneChoice === zone}
                className={zoneChoice === zone ? styles.chipActive : styles.chip}
                onClick={() => setZoneChoice(zone)}
                disabled={submitting}
              >
                {zone === zonePlan.device
                  ? t('setup.timezone.useDevice', { zone: homeZoneLabel(zone, i18n.language) })
                  : t('setup.timezone.useAddress', { zone: homeZoneLabel(zone, i18n.language) })}
              </button>
            ))}
          </div>
        </div>
      ) : zoneLine && (
        <p className={styles.fieldHint} aria-live="polite">{zoneLine}</p>
      )}

      <div className={styles.formActions}>
        <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={submitting}>
          {t('action.cancel')}
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={!canSubmit}>
          {submitting
            ? <><Loader2 size={14} className={styles.spin} /> {t('setup.saving')}</>
            : t('setup.submit')}
        </button>
      </div>
    </form>
  );
}
